import React, { useState, useEffect, useRef } from "react";
import { FaSearch, FaEye, FaArrowLeft, FaCalendarAlt } from "react-icons/fa";
import "./GoalsPage.css";
import { Link, useNavigate } from "react-router-dom";
import { buildDomainTags } from "./goalDomainTags";
import { API_BASE } from "../apiConfig";


// GOAL DOMAIN cell tags now live in goalDomainTags.js so the Goal Review page
// can render the identical tag without importing this page's stylesheet.
// Re-exported here because ClientHistory.js imports it from this module.
export { buildDomainTags };


const GoalsPage = () => {
  const navigate = useNavigate();
  const [globalSearch, setGlobalSearch] = useState("");
  const [filters] = useState({
    clientId: "",
    client: "",
    assessmentDate: "",
    smartGoal: "",
    goalDomain: "",
    targetDate: "",
    status: "",
    achievedBy: "",
    comments: "",
  });

  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "asc",
  });

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;

  // Date range filter (same behaviour as Continue Assessment page)
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const fromDateRef = useRef(null);
  const toDateRef = useRef(null);

  const token = localStorage.getItem("token");

  const fetchGoals = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${API_BASE}/therapist_api/get_goals_page.php`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({}),
        }
      );
      const json = await response.json();
      if (json.status && json.data) {
        setData(json.data.sort((a, b) => new Date(b.assessmentDate) - new Date(a.assessmentDate)));
      } else {
        setData([]);
      }
    } catch (error) {
      console.error("Error fetching goals data:", error);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGoals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleSort = (column) => {
    let direction = "asc";
    if (sortConfig.key === column && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key: column, direction });
  };

  const formatDate = (value) => {
    if (!value) return "";
    const datePart = String(value).split(" ")[0];
    const parts = datePart.split("-");
    if (parts.length !== 3) return value;
    const [year, month, day] = parts;
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    if (year && month && day) return `${parseInt(day)} ${months[parseInt(month) - 1]} ${year}`;
    return value;
  };

  useEffect(() => { setCurrentPage(1); }, [globalSearch, filters, fromDate, toDate]);

  // Filtering
  let filteredData = data.filter((row) => {
    // Global Search - matches ANY column (safe for numbers/null)
    const q = globalSearch.toLowerCase();
    const matchesGlobal =
      String(row.clientId ?? "").toLowerCase().includes(q) ||
      String(row.client ?? "").toLowerCase().includes(q) ||
      String(row.assessmentDate ?? "").toLowerCase().includes(q) ||
      String(row.appointmentType ?? "").toLowerCase().includes(q) ||
      String(row.smartGoal ?? "").toLowerCase().includes(q) ||
      String(row.goalDomain ?? "").toLowerCase().includes(q) ||
      String(row.targetDate ?? "").toLowerCase().includes(q) ||
      String(row.lastReviewDate ?? "").toLowerCase().includes(q) ||
      String(row.status ?? "").toLowerCase().includes(q) ||
      String(row.achievedBy ?? "").toLowerCase().includes(q) ||
      String(row.comments ?? "").toLowerCase().includes(q);

    // Date Range on Assessment Date
    let rowDate = null;
    if (row.assessmentDate) {
      rowDate = new Date(String(row.assessmentDate).split(" ")[0]);
    }

    const from = fromDate ? new Date(fromDate) : new Date("1900-01-01");
    const to = toDate ? new Date(toDate) : new Date("2100-12-31");
    if (toDate) to.setHours(23, 59, 59, 999);

    const matchesDateRange =
      rowDate && !isNaN(rowDate.getTime())
        ? rowDate >= from && rowDate <= to
        : true;

    return matchesGlobal && matchesDateRange;
  });

  // Sorting (fixed!)
  if (sortConfig.key) {
    filteredData = [...filteredData].sort((a, b) => {
      const x = a[sortConfig.key] || "";
      const y = b[sortConfig.key] || "";

      if (x < y) return sortConfig.direction === "asc" ? -1 : 1;
      if (x > y) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }

  const totalPages = Math.max(1, Math.ceil(filteredData.length / rowsPerPage));
  const paginatedData = filteredData.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  const goToPage = (page) => { if (page >= 1 && page <= totalPages) setCurrentPage(page); };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);
    if (end - start + 1 < maxVisible) start = Math.max(1, end - maxVisible + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  };

  return (
    <>
      <Link onClick={() => navigate(-1)} className="back-btn2" aria-label="Go back to dashboard">
        <FaArrowLeft className="back-icon" aria-hidden="true" />
        Previous page
      </Link>
      <main className="continue-container">
        <div className="common-page-header">
          <h1 className="common-page-title">Review Existing Goals</h1>
          <p className="common-page-subtitle">Track caregiver led goals here</p>
        </div>
        <div className="filter-section">
          <div className="search-box">
            <div className="common-search-box">
              <input
                type="text"
                placeholder="Search"
                value={globalSearch}
                onChange={(e) => setGlobalSearch(e.target.value)}
                aria-label="Search goals"
              />
              <FaSearch className="common-search-icon" />
            </div>
          </div>

          {/* From Date */}
          <div className="filter-date-range">
            <div className="filter-date-wrapper">
              <label htmlFor="goals-from-date" className="sr-only">From Date</label>
              <div className="filter-date-box" role="button" tabIndex="0" onClick={() => fromDateRef.current?.showPicker()} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fromDateRef.current?.showPicker(); } }} aria-label={`Filter from date${fromDate ? `: ${fromDate}` : ""}`} aria-haspopup="dialog">
                <span className={!fromDate ? "filter-date-placeholder" : "filter-date-value"}>
                  {fromDate || "From"}
                </span>
                <FaCalendarAlt className="filter-date-icon" />
                <input
                  type="date"
                  id="goals-from-date"
                  name="fromDate"
                  ref={fromDateRef}
                  value={fromDate}
                  onChange={(e) => {
                    setFromDate(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="filter-date-input"
                  aria-label="From date"
                />
              </div>
            </div>

            {/* To Date */}
            <div className="filter-date-wrapper">
              <label htmlFor="goals-to-date" className="sr-only">To Date</label>
              <div className="filter-date-box" role="button" tabIndex="0" onClick={() => toDateRef.current?.showPicker()} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toDateRef.current?.showPicker(); } }} aria-label={`Filter to date${toDate ? `: ${toDate}` : ""}`} aria-haspopup="dialog">
                <span className={!toDate ? "filter-date-placeholder" : "filter-date-value"}>
                  {toDate || "To"}
                </span>
                <FaCalendarAlt className="filter-date-icon" />
                <input
                  type="date"
                  id="goals-to-date"
                  name="toDate"
                  ref={toDateRef}
                  value={toDate}
                  onChange={(e) => {
                    setToDate(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="filter-date-input"
                  aria-label="To date"
                />
              </div>
            </div>
          </div>
        </div>



        <div className="table-wrapper gotable-wrapper">
          {loading ? (
            <div style={{ textAlign: "center", padding: "20px" }} role="status" aria-live="polite">
              Loading goals...
            </div>
          ) : (
                <table className="goals-table" style={{ marginBottom: "20px", tableLayout: "fixed" }}>
                  <caption className="sr-only">Goals data</caption>
              <colgroup>
                <col style={{ width: "14%" }} />
                <col style={{ width: "9%" }} />
                <col style={{ width: "14%" }} />
                <col style={{ width: "18%" }} />
                <col style={{ width: "7%" }} />
                <col style={{ width: "9%" }} />
                <col style={{ width: "9%" }} />
                <col style={{ width: "9%" }} />
                <col style={{ width: "11%" }} />
              </colgroup>
              <thead>
                <tr>
                  {[
                    { key: "client", label: "CLIENT" },
                    { key: "assessmentDate", label: "ASSESSED" },
                    { key: "smartGoal", label: "SMART GOAL" },
                    { key: "goalDomain", label: "GOAL DOMAIN" },
                    { key: "appointmentType", label: "SESSION" },
                    { key: "targetDate", label: "TARGET" },
                    { key: "lastReviewDate", label: "LAST REVIEW" },
                    { key: "status", label: "STATUS" },
                  ].map(({ key, label }) => (
                    <th
                      key={key}
                      scope="col"
                      onClick={() => handleSort(key)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handleSort(key);
                        }
                      }}
                      tabIndex="0"
                      aria-label={`Sort by ${label}${sortConfig?.key === key ? `, current order ${sortConfig.direction === "asc" ? "ascending" : "descending"}` : ""}`}
                      aria-sort={sortConfig?.key === key ? (sortConfig.direction === "asc" ? "ascending" : "descending") : "none"}
                    >
                      {label}
                    </th>
                  ))}
                  <th scope="col">ACTIONS</th>
                </tr>
              </thead>

              <tbody>
                {paginatedData.map((row, index) => (
                  <tr key={index}>
                    <td className="goals-td-client">
                      {/* Client rows open the dedicated Client History page,
                          which keeps every historical review update. */}
                      <Link
                        to={`/client-history?clientId=${row.clientDbId}&sessionId=${row.sessionId}`}
                        className="goals-client-history-link"
                        aria-label={`View client history for ${row.client}`}
                      >
                        <span className="goals-client-name">{row.client}</span>
                      </Link>
                      <span className="goals-client-id">{row.clientId}</span>
                    </td>

                    <td className="goals-td-center">{formatDate(row.assessmentDate)}</td>

                    <td className="goals-td-left">{row.smartGoal}</td>

                    <td className="goals-td-domain">
                      {buildDomainTags(row).map((tag, i) => (
                        <span key={i} className="goals-domain-tag">
                          {tag}
                        </span>
                      ))}
                    </td>

                    <td className="goals-td-center">
                      {row.appointmentType
                        ? row.appointmentType.charAt(0).toUpperCase() + row.appointmentType.slice(1).toLowerCase()
                        : ""}
                    </td>

                    <td className="goals-td-center">{formatDate(row.target_date || row.targetDate)}</td>

                    <td className="goals-td-center">{formatDate(row.lastReviewDate)}</td>

                    <td className="goals-td-status">
                      {row.status
                        ? row.status.split("-").map((s, i) => (
                            <span key={i} className="goals-status-badge">
                              {s.trim()}
                            </span>
                          ))
                        : "In progress - on track"}
                    </td>

                    <td className="goals-td-actions">
                      <div className="goals-actions-row">
                        <Link
                          to={`/goalreview?sessionId=${row.sessionId}&fromGoalsPage=1`}
                          className="goals-action-review"
                          aria-label={`Review goals for ${row.client}`}
                        >
                          Review
                        </Link>

                        {row.hasGoals ? (
                          <Link
                            to={`/goalsetdownload?sessionId=${row.sessionId}`}
                            className="goals-action-icon"
                            aria-label={`View goals for ${row.client}`}
                          >
                            <FaEye className="goals-eye-icon" />
                          </Link>
                        ) : (
                          <span className="goals-action-icon goals-action-disabled">
                            <FaEye className="goals-eye-icon" />
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <nav className="gopagination" aria-label="Pagination">
            <button disabled={currentPage === 1} onClick={() => goToPage(currentPage - 1)}>Previous</button>
            {getPageNumbers().map((p) => (
              <button key={p} className={p === currentPage ? 'active' : ''} aria-current={p === currentPage ? "page" : undefined} onClick={() => goToPage(p)}>{p}</button>
            ))}
            <button disabled={currentPage === totalPages} onClick={() => goToPage(currentPage + 1)}>Next</button>
          </nav>
        </div>
      </main>

    </>
  );
};

export default GoalsPage;
